import dayjs from 'dayjs'

import type { IReportPeriod } from '../Contracts/IReportPeriod'
import type { Dayjs } from 'dayjs'

/**
 * Період звіту в адресі (`SPEC-027` §3.4, К-04, Q3.5).
 *
 * - `?from=2026-10-01&to=2026-10-31` — цей період: посилання на звіт відкриває
 *   рівно те, що бачив автор;
 * - `?from=&to=` — «Увесь час» (порожні межі бек читає як «без меж»);
 * - параметрів немає або вони зіпсовані — поточний місяць.
 *
 * Зіпсоване значення не пускаємо до бека: замість 422 на ручному
 * редагуванні адреси людина бачить звіт за місяць і той самий місяць у фільтрі.
 */
export const REPORT_DAY_FORMAT = 'YYYY-MM-DD'

/** Формат поля періоду на екрані (`SPEC-027` §3.4). */
export const REPORT_DISPLAY_FORMAT = 'DD.MM.YYYY'

/** Найдовший період бека, роки (`ReportPeriod::MAX_YEARS`). */
export const REPORT_PERIOD_MAX_YEARS = 3

const DAY = /^\d{4}-\d{2}-\d{2}$/

const parseDay = (value: string): Dayjs | null => {
    if (!DAY.test(value)) return null

    const day = dayjs(value, REPORT_DAY_FORMAT)

    // `dayjs` без strict-плагіна перекочує 2026-02-30 у березень — ловимо зворотним форматом
    return day.isValid() && day.format(REPORT_DAY_FORMAT) === value ? day : null
}

export const ALL_TIME: IReportPeriod = { from: null, to: null }

export const isAllTime = (period: IReportPeriod): boolean => period.from === null && period.to === null

export const monthPeriod = (day: Dayjs): IReportPeriod => ({
    from: day.startOf('month').format(REPORT_DAY_FORMAT),
    to: day.endOf('month').format(REPORT_DAY_FORMAT),
})

/** Період з адреси; `today` — параметром, щоб тест не залежав від дати прогону. */
export const periodFromSearch = (params: URLSearchParams, today: Dayjs = dayjs()): IReportPeriod => {
    const from = params.get('from')
    const to = params.get('to')

    if (from === '' && to === '') return ALL_TIME

    if (from !== null && to !== null) {
        const start = parseDay(from)
        const end = parseDay(to)

        if (
            start !== null &&
            end !== null &&
            !start.isAfter(end) &&
            // Та сама межа, що в бека: `to + 1 день` не пізніше `from + 3 роки`
            !end.add(1, 'day').isAfter(start.add(REPORT_PERIOD_MAX_YEARS, 'year'))
        ) {
            return { from, to }
        }
    }

    return monthPeriod(today)
}

/** Записує період у параметри адреси, решту параметрів лишає як є. */
export const periodToSearch = (period: IReportPeriod, params: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams(params)

    next.set('from', period.from ?? '')
    next.set('to', period.to ?? '')

    return next
}

/** Параметри запиту до бека: без меж — без параметрів. */
export const periodToQuery = (period: IReportPeriod): Record<string, string> =>
    period.from !== null && period.to !== null ? { from: period.from, to: period.to } : {}

export interface IReportPeriodPreset {
    label: string
    period: IReportPeriod
}

/** Пресети фільтра (`SPEC-027` §3.4): «Цей місяць», «Минулий місяць», «Цей рік», «Увесь час». */
export const reportPeriodPresets = (today: Dayjs = dayjs()): IReportPeriodPreset[] => [
    { label: 'Цей місяць', period: monthPeriod(today) },
    { label: 'Минулий місяць', period: monthPeriod(today.subtract(1, 'month')) },
    {
        label: 'Цей рік',
        period: {
            from: today.startOf('year').format(REPORT_DAY_FORMAT),
            to: today.endOf('year').format(REPORT_DAY_FORMAT),
        },
    },
    { label: 'Увесь час', period: ALL_TIME },
]

export const samePeriod = (a: IReportPeriod, b: IReportPeriod): boolean => a.from === b.from && a.to === b.to
